import { useNavigate } from "react-router-dom";
import { useRoles } from "@src/presentation/hooks/rbac";
import { Skeleton } from "@heroui/react";
import { usePermission } from "@src/presentation/hooks/usePermission";
import { PermissionPlaceholder } from "@src/presentation/components/PermissionPlaceholder";

export function RolesSummaryCard() {
    const navigate = useNavigate();
    const { hasPermission } = usePermission();
    const canViewRoles = hasPermission("role:read");

    // Early return if no permission
    if (!canViewRoles) {
        return <PermissionPlaceholder title="Roles & Permissions" description="You need 'role:read' permission to view roles." />;
    }

    const { data: roles = [], isLoading, error } = useRoles();

    const totalRoles = roles.length;
    const activeRoles = roles.filter((r) => r.isActive).length;
    const totalPermissions = new Set(roles.flatMap((r) => r.permissions)).size;

    return (
        <div className="rounded border border-border bg-card shadow-none hover:shadow-custom transition-shadow">
            {/* Header with Stats */}
            <div className="border-b border-border p-6">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
                            <svg className="h-5 w-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                                />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-foreground">Roles & Permissions</h2>
                            <p className="text-sm text-muted-foreground">Access control configuration</p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate("/rbac/roles")}
                        className="rounded border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                    >
                        Manage Roles
                    </button>
                </div>

                {/* Stats Grid */}
                <div className="mt-6 grid grid-cols-3 gap-4">
                    <div className="rounded bg-background p-4">
                        <p className="text-xs font-medium text-muted-foreground">Total Roles</p>
                        {isLoading ? (
                            <Skeleton className="mt-1 h-8 w-12 rounded-lg" />
                        ) : error ? (
                            <p className="mt-1 text-2xl font-bold text-destructive">--</p>
                        ) : (
                            <p className="mt-1 text-2xl font-bold text-foreground">{totalRoles}</p>
                        )}
                    </div>
                    <div className="rounded bg-background p-4">
                        <p className="text-xs font-medium text-muted-foreground">Active Roles</p>
                        {isLoading ? (
                            <Skeleton className="mt-1 h-8 w-12 rounded-lg" />
                        ) : error ? (
                            <p className="mt-1 text-2xl font-bold text-destructive">--</p>
                        ) : (
                            <p className="mt-1 text-2xl font-bold text-foreground">{activeRoles}</p>
                        )}
                    </div>
                    <div className="rounded bg-background p-4">
                        <p className="text-xs font-medium text-muted-foreground">Permissions</p>
                        {isLoading ? (
                            <Skeleton className="mt-1 h-8 w-12 rounded-lg" />
                        ) : error ? (
                            <p className="mt-1 text-2xl font-bold text-destructive">--</p>
                        ) : (
                            <p className="mt-1 text-2xl font-bold text-foreground">{totalPermissions}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Roles List */}
            <div className="p-6">
                <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-foreground">Recent Roles</h3>
                    <button
                        onClick={() => navigate("/rbac/permissions")}
                        className="text-xs font-medium text-primary hover:underline"
                    >
                        View Permissions
                    </button>
                </div>

                {isLoading ? (
                    <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-16 w-full rounded-lg" />
                        ))}
                    </div>
                ) : error ? (
                    <div className="py-8 text-center">
                        <p className="text-sm text-destructive">Failed to load roles</p>
                    </div>
                ) : roles.length === 0 ? (
                    <div className="py-8 text-center">
                        <p className="text-sm text-muted-foreground">No roles found</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {roles.slice(0, 5).map((role) => (
                            <div
                                key={role.roleId}
                                className="flex cursor-pointer items-center justify-between rounded border border-border bg-background p-3 transition-all hover:border-primary/50 hover:shadow-sm"
                                onClick={() => navigate("/rbac/roles")}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="flex h-8 w-8 items-center justify-center rounded bg-accent/10">
                                        <svg className="h-4 w-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                                            />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-foreground">{role.displayName}</p>
                                        <p className="text-xs text-muted-foreground">{role.description}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="text-right">
                                        <p className="text-xs text-muted-foreground">{role.permissions.length} permissions</p>
                                        {role.isActive ? (
                                            <span className="text-xs text-success">Active</span>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">Inactive</span>
                                        )}
                                    </div>
                                    <svg className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
